import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Dices, Instagram, ArrowRight, ExternalLink } from "lucide-react";

const hyenaPosts = [
  "https://www.instagram.com/p/DZ5NmfKCqxD/",
  "https://www.instagram.com/p/DZmasnqsDok/",
];

export const Hyena = () => {
  useEffect(() => {
    const SCRIPT_ID = "instagram-embed-script";
    const process = () => {
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process();
      }
    };
    let script = document.getElementById(SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = "https://www.instagram.com/embed.js";
      script.onload = process;
      document.body.appendChild(script);
    } else {
      process();
    }
  }, []);

  return (
    <section
      id="hyena"
      data-testid="hyena-section"
      className="relative py-24 md:py-32 overflow-hidden border-t border-zinc-900 bg-black"
    >
      <div className="absolute inset-0">
        <img
          src="/shop/gmaps-3.jpg"
          alt="Community at Ted Coffee & Fun Spot"
          className="w-full h-full object-cover shot-enhance opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Dices className="w-4 h-4 text-sky-400" />
              <span className="font-mono text-[11px] tracking-[0.3em] text-sky-400 uppercase">
                Tabletop & D&D · Community
              </span>
            </div>

            <h2 className="font-display text-white text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
              Hyena <br />
              <span className="text-sky-400 neon-glow">Dungeon.</span>
            </h2>

            <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
              Εχουμε τη δικη μας ομαδα και κοινοτητα στο{" "}
              <span className="text-white font-semibold">Dungeons & Dragons</span>.
              Αν εισαι λατρης των RPG και του επιτραπεζιου αφηγηματικου παιχνιδιου,
              μπες στην παρεα μας. Roll for initiative και αφησε τα ζαρια να αποφασισουν.
            </p>

            <a
              href="https://www.instagram.com/hyenadungeon/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="hyena-instagram"
              className="mt-10 inline-flex items-center gap-3 bg-white text-black font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-sky-400 transition-colors group"
            >
              <Instagram className="w-4 h-4" />
              Follow Hyena Dungeon
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>

            <Link
              to="/hyena-dungeon"
              data-testid="hyena-fullpage-cta"
              className="mt-3 ml-0 sm:ml-3 inline-flex items-center gap-3 border-2 border-red-500 text-red-300 font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-red-500 hover:text-white transition-colors group"
            >
              <ExternalLink className="w-4 h-4" />
              Full Hyena Page
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-md">
              <RollStat label="Party" value="4 – 6" />
              <RollStat label="Sessions" value="Weekly" />
              <RollStat label="Vibe" value="Chaotic Good" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-6"
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.3em] text-sky-400 uppercase">
                From the campaign / IG
              </span>
              <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
                02 posts
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {hyenaPosts.map((url, i) => (
                <div
                  key={url}
                  data-testid={`hyena-ig-embed-${i}`}
                  className="ig-embed-wrap relative border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-sky-400/50 transition-colors"
                >
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={`${url}?utm_source=ig_embed&utm_campaign=loading`}
                    data-instgrm-version="14"
                    style={{
                      background: "#0A0A0A",
                      border: 0,
                      margin: 0,
                      maxWidth: "100%",
                      minWidth: "260px",
                      padding: 0,
                      width: "100%",
                    }}
                  >
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="block p-6 text-zinc-400 hover:text-sky-400 transition-colors text-sm"
                    >
                      View post on Instagram ↗
                    </a>
                  </blockquote>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const RollStat = ({ label, value }) => (
  <div className="border border-zinc-800 bg-black/60 backdrop-blur-md p-4">
    <div className="font-mono text-[10px] tracking-[0.3em] text-sky-400 uppercase mb-1.5">
      {label}
    </div>
    <div className="font-display text-white text-lg leading-tight">{value}</div>
  </div>
);
